// src/app/features/auth/components/company-registration-form-view.tsx
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CompanyFieldView from "./company-field-view"; // Import the CompanyFieldView component
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import { useEffect, useState } from "react";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { isNull } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";
import useRegisterCompany from "@/hooks/use-register-company";
import { createCompanySchema } from "@/schema";
import { toast } from "react-toastify";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  useTheme,
  alpha,
  Fade,
} from "@mui/material";
import {
  Business,
  LocationOn,
  Description,
  AttachFile,
  Edit,
  Save,
  Cancel,
} from "@mui/icons-material";
import useProfile from "@/hooks/use-profile";
import { getMyCompany } from "@/store/authSlice";
import { useDispatch } from "react-redux";

// import { CategoryOption } from "@/pages/seller/articles";
// import useOffer from "@/hooks/useTender";
//import { Button } from "@mui/material";

interface Region {
  region_id: number;
  name_ar: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

const CompanyFormView = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const CompanySchema = createCompanySchema(t as (key: string) => string);
  const { registerCompany, updateCompany, isSaveLoading, error } =
    useRegisterCompany();
  const [showStampedAuthorizationForm, setShowStampedAuthorizationForm] =
    useState<boolean>(false);
  const [regions, setRegions] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [isEditMode, setIsEditMode] = useState(true);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const theme = useTheme();
  const { getUserProfile } = useProfile();
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!myCompany?.id) {
        try {
          const userData = await getUserProfile();
          if (userData.data.company) {
            setIsEditMode(false);
            dispatch(getMyCompany(userData.data.company));
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      }
    };

    fetchUserProfile();
  }, [myCompany]);
  const form = useForm<z.infer<typeof CompanySchema>>({
    resolver: zodResolver(CompanySchema),
    defaultValues: {
      nameAr: "",
      nameEn: "",
      website: "",
      address: "",
      region: "",
      city: "",
      registrationNumber: "",
      ownerType: "Owner",
      stampedAuthorizationFormUrl: null,
      plaseOfIssue: "",
      turnover: 0,
      type: "",
      activities: "",
      categories: "",
      creationDate: "",
      registrationExpirationDate: "",
    },
  });

  useEffect(() => {
    if (!isNull(myCompany)) {
      form.reset({
        ...form.getValues(),
        ...myCompany,
        region: myCompany?.region?.toString() || "",
        city: myCompany?.city?.toString() || "",
      });
    }
  }, [myCompany]);

  const { watch } = form;
  const ownerType = watch("ownerType");

  useEffect(() => {
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );
  }, []);

  useEffect(() => {
    const selectedRegion = form.watch("region");
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city: City) => city.region_id === parseInt(selectedRegion))
        .map((city: City) => ({
          value: city.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [form.watch("region")]);

  const onSubmit = async (values: z.infer<typeof CompanySchema>) => {
    try {
      const formData = new FormData();

      const data = Object.entries(values).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ""
      );
      for (const [key, value] of data) {
        if (
          key !== "authorizationFile" &&
          key !== "registeringFile" &&
          key !== "logoMedia"
        ) {
          formData.append(key, String(value) || "");
        }
      }

      if (values.authorizationFile instanceof File) {
        formData.append("authorizationFile", values.authorizationFile);
      }

      if (values.registeringFile instanceof File) {
        formData.append("registeringFile", values.registeringFile);
      }

      if (values.logoMedia instanceof File) {
        formData.append("logoMedia", values.logoMedia);
      }

      if (myCompany?.userId) {
        try {
          const res = await updateCompany(formData, myCompany.id);
          if (res) {
            toast.success(t("alerts.success.companyUpdated"));
          }
          setIsEditMode(false);
        } catch (err) {
          console.error("Error during registration:", err);
        }
      } else {
        try {
          const res = await registerCompany(formData);
          if (res) {
            toast.success(t("alerts.success.companyRegistration"));
          }
        } catch (err) {
          console.error("Error during registration:", err);
        }
      }
    } catch (err) {
      console.error("Error during registration:", err);
    }
  };

  useEffect(() => {
    if (ownerType === "Authorized") {
      setShowStampedAuthorizationForm(true);
    } else {
      setShowStampedAuthorizationForm(false);
    }
  }, [ownerType]);

  const handleEditToggle = () => {
    if (isEditMode) {
      form.reset();
    }
    setIsEditMode(!isEditMode);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
          {/* Header with Edit Mode Indicator */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              boxShadow: "none",
              borderRadius: 3,
              bgcolor: isEditMode
                ? alpha(theme.palette.warning.main, 0.05)
                : "background.paper",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="h5" fontWeight={600}>
                    {t("company.info")}
                  </Typography>
                  {myCompany && isEditMode && (
                    <Chip
                      label={t("actions.editing") || "Editing"}
                      color="warning"
                      size="small"
                      icon={<Edit />}
                    />
                  )}
                </Box>
                {myCompany && (
                  <Button
                    variant={isEditMode ? "outlined" : "contained"}
                    startIcon={isEditMode ? <Cancel /> : <Edit />}
                    onClick={handleEditToggle}
                    className={!isEditMode ? "!bg-zinc-900" : ""}
                    color={isEditMode ? "error" : "primary"}
                    sx={{ borderRadius: 2 }}
                  >
                    {isEditMode ? t("actions.cancel") : t("actions.edit")}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Basic Information Section */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              boxShadow: "none",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <Business color="secondary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600} color="secondary">
                  {t("company.fields.basicInfo") || "Basic Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="nameAr"
                    label={`${t("company.fields.name")}*`}
                    placeholder={t("company.placeholders.chooseName")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="nameEn"
                    label={`${t("company.fields.nameEn")}*`}
                    placeholder={t("company.placeholders.enterNameEn")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CompanyFieldView
                    name="website"
                    label={t("company.fields.website")}
                    placeholder={t("company.placeholders.enterWebsite")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Location Information Section */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              boxShadow: "none",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <LocationOn color="secondary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600} color="secondary">
                  {t("company.fields.location")}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <CompanyFieldView
                    name="address"
                    label={t("company.fields.address")}
                    placeholder={t("company.placeholders.enterAddress")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="region"
                    label={t("company.placeholders.selectRegion") + "*"}
                    placeholder=""
                    inputType="combobox"
                    options={regions}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="city"
                    label={t("company.placeholders.selectCity") + "*"}
                    placeholder={t("company.placeholders.selectCity")}
                    inputType="combobox"
                    options={cities}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Registration Information Section */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              boxShadow: "none",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <Description color="secondary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600} color="secondary">
                  {t("company.fields.registrationInfo") ||
                    "Registration Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <CompanyFieldView
                    name="ownerType"
                    label={t("company.fields.ownerType")}
                    placeholder={t("company.fields.ownerType")}
                    inputType="radio"
                    options={[
                      { value: "Owner", label: t("company.fields.owner") },
                      {
                        value: "Authorized",
                        label: t("company.fields.authorized"),
                      },
                    ]}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                {showStampedAuthorizationForm && (
                  <>
                    <Grid item xs={12} md={4}>
                      <CompanyFieldView
                        name="stampedAuthorizationFormUrl"
                        label={t("company.fields.authorizationFormId")}
                        placeholder={t(
                          "company.placeholders.enterAuthorizationId"
                        )}
                        formControl={form.control}
                        disabled={!isEditMode}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <CompanyFieldView
                        name="authorizationFile"
                        label={t("company.fields.authorizationFile")}
                        placeholder={t(
                          "company.placeholders.chooseAuthorizationFile"
                        )}
                        inputType="Attachment"
                        formControl={form.control}
                        disabled={!isEditMode}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <CompanyFieldView
                    name="registrationNumber"
                    label={`${t("company.fields.commercialRegistration")}*`}
                    placeholder={t(
                      "company.placeholders.enterCommercialRegNumber"
                    )}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CompanyFieldView
                    name="registeringFile"
                    label={t("company.fields.registrationFile")}
                    placeholder={t(
                      "company.placeholders.chooseCommercialRegFile"
                    )}
                    inputType="Attachment"
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CompanyFieldView
                    name="taxInformation"
                    label={`${t("company.fields.taxInfo")}*`}
                    placeholder={t("company.placeholders.enterTaxInfo")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <CompanyFieldView
                    name="plaseOfIssue"
                    label={t("company.fields.placeOfIssue")}
                    placeholder={t("company.placeholders.enterIssuePlace")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CompanyFieldView
                    name="creationDate"
                    label={t("company.fields.registrationDate")}
                    placeholder={t(
                      "company.placeholders.enterRegistrationDate"
                    )}
                    inputType="date"
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CompanyFieldView
                    name="registrationExpirationDate"
                    label={t("company.fields.expiryDate")}
                    placeholder={t("company.placeholders.enterExpiryDate")}
                    inputType="date"
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Business Information Section */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              boxShadow: "none",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <AttachFile color="secondary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600} color="secondary">
                  {t("company.fields.businessInfo") || "Business Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="turnover"
                    label={
                      authUser.role === ROLE.SELLER
                        ? t("company.fields.annualSales")
                        : t("company.fields.annualPurchases")
                    }
                    placeholder={
                      authUser.role === ROLE.SELLER
                        ? t("company.placeholders.enterAnnualSales")
                        : t("company.placeholders.enterAnnualPurchases")
                    }
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="employeesNumber"
                    label={t("company.fields.employeesNumber")}
                    placeholder={t("company.placeholders.enterEmployeeCount")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="categories"
                    label={t("company.fields.categories")}
                    placeholder={t("company.placeholders.enterCategories")}
                    inputType="textarea"
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="activities"
                    label={t("company.fields.activities")}
                    placeholder={t("company.placeholders.enterActivities")}
                    inputType="textarea"
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompanyFieldView
                    name="logoMedia"
                    label={t("company.fields.logo")}
                    placeholder={t("company.placeholders.chooseLogo")}
                    inputType="file"
                    formControl={form.control}
                    defaultImage={myCompany?.logoMedia || ""}
                    disabled={!isEditMode}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Error Display */}
          {error && (
            <Fade in={!!error}>
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${theme.palette.error.main}`,
                  borderRadius: 2,
                }}
              >
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Paper>
            </Fade>
          )}

          {/* Save Button */}
          {isEditMode && (
            <Fade in={isEditMode}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 3,
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleEditToggle}
                      disabled={isSaveLoading}
                      sx={{ borderRadius: 2 }}
                    >
                      {t("actions.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                      disabled={isSaveLoading}
                      sx={{ borderRadius: 2 }}
                    >
                      {isSaveLoading ? t("actions.saving") : t("actions.save")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          )}
        </Box>
      </form>
    </Form>
  );
};

export default CompanyFormView;
